export {
  mergeMissingProductRichTextFields,
  mergeMissingRichTextDescriptionFields,
  mergeMissingRichTextFields,
} from './rich-text-merge'

const ALLOWED_TAGS = new Set(['p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li'])
const BLOCK_TAGS = new Set([
  'p',
  'ul',
  'ol',
  'li',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'section',
  'article',
  'header',
  'footer',
  'aside',
  'main',
  'nav',
  'address',
  'pre',
  'figure',
  'figcaption',
])
const TAG_ALIASES: Record<string, string> = {
  b: 'strong',
  i: 'em',
}
const DROP_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'svg',
  'math',
  'meta',
  'link',
  'title',
  'noscript',
])

/**
 * Recipes opt in to `preserveSpaces`: florists line recipes up as grids with runs of spaces, so the
 * spaces they type stay. Text our editor marks `white-space: pre-wrap` keeps every space; elsewhere,
 * regular whitespace still collapses as a browser shows it, and no-break spaces stay. A run of
 * spaces is stored as no-break and regular spaces taking turns, starting with a no-break space
 * ("8&nbsp; &nbsp; 12"), and a space that starts typed text as a no-break space: browsers, our
 * editor (Lexical) and the sanitizers collapse only regular spaces that sit together and drop a
 * regular space at the start of a line. The plain text turns them back into spaces. Everything else
 * (descriptions) collapses runs of whitespace. The API's `RichTextDescription` follows the same
 * rules; `api/tests/Fixtures/rich-text-sanitizer-contract.json` checks both.
 */
export type RichTextOptions = {
  preserveSpaces?: boolean
}

type NormalizeNodeOptions = {
  normalizeHref: (href: string | null) => string | null
  decorateLink?: (element: Element, href: string) => void
  preserveSpaces: boolean
}

const NO_BREAK_SPACE = '\u00a0'
const PREFORMATTED_STYLE_PATTERN =
  /(?:^|;)\s*white-space\s*:\s*(?:pre|pre-wrap|break-spaces)\s*(?:;|$)/i

function isTextNode(node: Node | null | undefined): node is Text {
  return node?.nodeType === Node.TEXT_NODE
}

function isElementNode(node: Node | null | undefined): node is Element {
  return node?.nodeType === Node.ELEMENT_NODE
}

// trim() also removes no-break spaces, which hold a recipe's spacing.
function hasVisibleText(text: string, preserveSpaces: boolean): boolean {
  return preserveSpaces ? /[^ \t\n\r]/.test(text) : text.trim() !== ''
}

function cleanupInlineText(text: string, preserveSpaces: boolean): string {
  // Kept spaces stay no-break spaces until the end, so these passes never collapse them.
  const cleanedText = (preserveSpaces ? text : text.replace(/\u00a0/g, ' '))
    .replace(/[ \t\r]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')

  return preserveSpaces ? cleanedText.replace(/^[ \t\n\r]+|[ \t\n\r]+$/g, '') : cleanedText.trim()
}

/**
 * The plain text of HTML whose spaces were kept: no-break spaces become spaces, each line loses its
 * trailing spaces, and blank lines between blocks stay one blank line.
 */
function plainTextKeepingSpaces(text: string): string {
  return text
    .replaceAll(NO_BREAK_SPACE, ' ')
    .replace(/ +$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+|\n+$/g, '')
}

/**
 * Every space as one that HTML keeps: a run of spaces becomes no-break and regular spaces taking
 * turns, starting with a no-break space, and a space that starts the text becomes a no-break space.
 * A single space inside the text stays as it is.
 */
function spacesThatSurvive(text: string): string {
  return text.replace(/[ \u00a0]+/g, (run: string, offset: number) =>
    run.length === 1 && offset > 0
      ? run
      : Array.from(run, (_, index) => (index % 2 === 0 ? NO_BREAK_SPACE : ' ')).join('')
  )
}

/** Text our editor marks `white-space: pre-wrap` (or `pre`), where every space shows. */
function isPreformatted(node: Node): boolean {
  for (let parent = node.parentElement; parent; parent = parent.parentElement) {
    if (PREFORMATTED_STYLE_PATTERN.test(parent.getAttribute('style') ?? '')) {
      return true
    }
  }

  return false
}

function textNodeValueKeepingSpaces(node: Text): string {
  const text = node.textContent || ''

  if (isPreformatted(node)) {
    return spacesThatSurvive(text.replace(/[\t\n\r\f]/g, ' '))
  }

  // Regular whitespace collapses as a browser shows it; no-break spaces show, so they stay.
  const collapsedText = text.replace(/[ \t\n\r\f]+/g, ' ')

  return hasVisibleText(collapsedText, true) ? collapsedText : ''
}

function inlineTextFromNode(node: Node | null | undefined, preserveSpaces: boolean): string {
  if (!node) {
    return ''
  }

  if (isTextNode(node)) {
    return node.textContent || ''
  }

  if (!isElementNode(node)) {
    return ''
  }

  if (node.tagName === 'BR') {
    return '\n'
  }

  let text = ''

  for (const childNode of node.childNodes) {
    if (isElementNode(childNode)) {
      const childTagName = childNode.tagName

      if (childTagName === 'UL' || childTagName === 'OL') {
        continue
      }
    }

    text += inlineTextFromNode(childNode, preserveSpaces)
  }

  return cleanupInlineText(text, preserveSpaces)
}

function listItemTextFromNode(
  node: Element,
  listType: 'ul' | 'ol',
  depth: number,
  index: number,
  preserveSpaces: boolean
): string {
  let inlineContent = ''
  const nestedBlocks: string[] = []

  for (const childNode of node.childNodes) {
    if (isElementNode(childNode)) {
      const childTagName = childNode.tagName

      if (childTagName === 'UL' || childTagName === 'OL') {
        const nestedText = listTextFromNode(childNode, depth + 1, preserveSpaces)

        if (nestedText) {
          nestedBlocks.push(nestedText)
        }

        continue
      }
    }

    inlineContent += inlineTextFromNode(childNode, preserveSpaces)
  }

  inlineContent = cleanupInlineText(inlineContent, preserveSpaces)

  if (!inlineContent && nestedBlocks.length === 0) {
    return ''
  }

  if (!inlineContent) {
    return nestedBlocks.join('\n')
  }

  const prefix = `${'  '.repeat(depth)}${listType === 'ol' ? `${index}. ` : '- '}`
  const line = `${prefix}${inlineContent}`.trimEnd()

  return [line, ...nestedBlocks].filter((part) => part.trim() !== '').join('\n')
}

function listTextFromNode(node: Element, depth: number, preserveSpaces: boolean): string {
  const items: string[] = []
  const listType = node.tagName === 'OL' ? 'ol' : 'ul'
  let index = 1

  for (const childNode of node.childNodes) {
    if (!isElementNode(childNode)) {
      continue
    }

    const listItemText =
      childNode.tagName === 'LI' || hasMeaningfulChildContent(childNode, preserveSpaces)
        ? listItemTextFromNode(childNode, listType, depth, index, preserveSpaces)
        : ''

    if (!listItemText) {
      continue
    }

    items.push(listItemText)
    index += 1
  }

  return items
    .filter((item) => item.trim() !== '')
    .join('\n')
    .trim()
}

function collectBlocksFromContainer(node: Node, depth: number, preserveSpaces: boolean): string[] {
  const blocks: string[] = []
  let inlineContent = ''

  for (const childNode of node.childNodes) {
    if (isTextNode(childNode)) {
      inlineContent += childNode.textContent || ''

      continue
    }

    if (!isElementNode(childNode)) {
      continue
    }

    if (childNode.tagName === 'P') {
      const inlineText = cleanupInlineText(inlineContent, preserveSpaces)

      if (inlineText) {
        blocks.push(inlineText)
      }

      inlineContent = ''
      const text = inlineTextFromNode(childNode, preserveSpaces)

      if (text) {
        blocks.push(text)
      }

      continue
    }

    if (childNode.tagName === 'UL' || childNode.tagName === 'OL') {
      const inlineText = cleanupInlineText(inlineContent, preserveSpaces)

      if (inlineText) {
        blocks.push(inlineText)
      }

      inlineContent = ''
      const listText = listTextFromNode(childNode, depth, preserveSpaces)

      if (listText) {
        blocks.push(listText)
      }

      continue
    }

    if (childNode.tagName === 'LI') {
      const inlineText = cleanupInlineText(inlineContent, preserveSpaces)

      if (inlineText) {
        blocks.push(inlineText)
      }

      inlineContent = ''
      const listItemText = listItemTextFromNode(childNode, 'ul', depth, 1, preserveSpaces)

      if (listItemText) {
        blocks.push(listItemText)
      }

      continue
    }

    if (isBlockTag(childNode.tagName)) {
      const inlineText = cleanupInlineText(inlineContent, preserveSpaces)

      if (inlineText) {
        blocks.push(inlineText)
      }

      inlineContent = ''
      blocks.push(...collectBlocksFromContainer(childNode, depth, preserveSpaces))
      continue
    }

    inlineContent += inlineTextFromNode(childNode, preserveSpaces)
  }

  const inlineText = cleanupInlineText(inlineContent, preserveSpaces)

  if (inlineText) {
    blocks.push(inlineText)
  }

  return blocks
}

function blocksFromNode(node: Node, preserveSpaces: boolean): string[] {
  return collectBlocksFromContainer(node, 0, preserveSpaces)
}

function parseRichTextHtml(html: unknown): Document | null {
  if (typeof html !== 'string' || !html.trim() || typeof DOMParser === 'undefined') {
    return null
  }

  return new DOMParser().parseFromString(html, 'text/html')
}

function normalizeTextNodeValue(text: string): string {
  const normalizedText = text.replace(/\s+/g, ' ')

  return normalizedText.trim() ? normalizedText : ''
}

function isInlineSibling(node: Node | null, preserveSpaces: boolean): boolean {
  if (!node) {
    return false
  }

  if (isTextNode(node)) {
    return hasVisibleText(node.textContent || '', preserveSpaces)
  }

  if (!isElementNode(node)) {
    return false
  }

  const tagName = TAG_ALIASES[node.tagName.toLowerCase()] || node.tagName.toLowerCase()

  return !BLOCK_TAGS.has(tagName) && hasMeaningfulChildContent(node, preserveSpaces)
}

function hasAdjacentInlineSiblings(node: Node, preserveSpaces: boolean): boolean {
  let previousSibling = node.previousSibling

  while (
    isTextNode(previousSibling) &&
    !hasVisibleText(previousSibling.textContent || '', preserveSpaces)
  ) {
    previousSibling = previousSibling.previousSibling
  }

  let nextSibling = node.nextSibling

  while (
    isTextNode(nextSibling) &&
    !hasVisibleText(nextSibling.textContent || '', preserveSpaces)
  ) {
    nextSibling = nextSibling.nextSibling
  }

  return (
    isInlineSibling(previousSibling, preserveSpaces) && isInlineSibling(nextSibling, preserveSpaces)
  )
}

function hasMeaningfulChildContent(node: Node, preserveSpaces: boolean): boolean {
  for (const childNode of node.childNodes) {
    if (isTextNode(childNode) && hasVisibleText(childNode.textContent || '', preserveSpaces)) {
      return true
    }

    if (isElementNode(childNode)) {
      if (childNode.tagName === 'BR' || hasMeaningfulChildContent(childNode, preserveSpaces)) {
        return true
      }
    }
  }

  return false
}

function hasMeaningfulListItemContent(node: Node, preserveSpaces: boolean): boolean {
  for (const childNode of node.childNodes) {
    if (isTextNode(childNode) && hasVisibleText(childNode.textContent || '', preserveSpaces)) {
      return true
    }

    if (!isElementNode(childNode)) {
      continue
    }

    const tagName = childNode.tagName.toLowerCase()

    if (
      (tagName === 'ul' || tagName === 'ol') &&
      hasMeaningfulChildContent(childNode, preserveSpaces)
    ) {
      return true
    }

    if (tagName === 'br') {
      continue
    }

    if (hasMeaningfulChildContent(childNode, preserveSpaces)) {
      return true
    }
  }

  return false
}

function sanitizeHref(href: string | null): string | null {
  if (typeof href !== 'string') {
    return null
  }

  const normalizedHref = href.trim()

  if (!normalizedHref) {
    return null
  }

  let url: URL

  try {
    url = new URL(normalizedHref)
  } catch {
    return null
  }

  const scheme = url.protocol.replace(':', '').toLowerCase()

  return ['http', 'https', 'mailto', 'tel'].includes(scheme) ? normalizedHref : null
}

function comparableHref(href: string | null): string | null {
  if (typeof href !== 'string') {
    return null
  }

  const normalizedHref = href.trim()

  return normalizedHref || null
}

function appendNormalizedNode(
  outputDocument: Document,
  parentNode: Element,
  node: Node,
  options: NormalizeNodeOptions
): void {
  if (isTextNode(node)) {
    const text = options.preserveSpaces
      ? textNodeValueKeepingSpaces(node)
      : normalizeTextNodeValue(node.textContent || '')

    if (text) {
      parentNode.append(outputDocument.createTextNode(text))
    } else if (hasAdjacentInlineSiblings(node, options.preserveSpaces)) {
      parentNode.append(outputDocument.createTextNode(' '))
    }

    return
  }

  if (!isElementNode(node)) {
    return
  }

  const tagName = node.tagName.toLowerCase()
  const normalizedTagName = TAG_ALIASES[tagName] || tagName

  if (DROP_TAGS.has(normalizedTagName)) {
    return
  }

  if (!ALLOWED_TAGS.has(normalizedTagName)) {
    if (isBlockTag(tagName)) {
      appendUnsupportedBlockChildren(outputDocument, parentNode, node, options)
      return
    }

    for (const childNode of node.childNodes) {
      appendNormalizedNode(outputDocument, parentNode, childNode, options)
    }

    return
  }

  if (normalizedTagName === 'a') {
    const href = options.normalizeHref(node.getAttribute('href'))

    if (!href) {
      for (const childNode of node.childNodes) {
        appendNormalizedNode(outputDocument, parentNode, childNode, options)
      }

      return
    }

    const element = outputDocument.createElement('a')
    element.setAttribute('href', href)

    options.decorateLink?.(element, href)

    for (const childNode of node.childNodes) {
      appendNormalizedNode(outputDocument, element, childNode, options)
    }

    if (hasMeaningfulChildContent(element, options.preserveSpaces)) {
      parentNode.append(element)
    }

    return
  }

  const element = outputDocument.createElement(normalizedTagName)

  for (const childNode of node.childNodes) {
    appendNormalizedNode(outputDocument, element, childNode, options)
  }

  if (
    normalizedTagName === 'br' ||
    (normalizedTagName === 'li'
      ? hasMeaningfulListItemContent(element, options.preserveSpaces)
      : hasMeaningfulChildContent(element, options.preserveSpaces))
  ) {
    parentNode.append(element)
  }
}

function decorateSanitizedLink(element: Element, href: string): void {
  if (href.startsWith('http://') || href.startsWith('https://')) {
    element.setAttribute('target', '_blank')
    element.setAttribute('rel', 'noopener noreferrer')
  }
}

function appendSanitizedNode(
  outputDocument: Document,
  parentNode: Element,
  node: Node,
  preserveSpaces: boolean
): void {
  appendNormalizedNode(outputDocument, parentNode, node, {
    normalizeHref: sanitizeHref,
    decorateLink: decorateSanitizedLink,
    preserveSpaces,
  })
}

function appendComparableNode(
  outputDocument: Document,
  parentNode: Element,
  node: Node,
  preserveSpaces: boolean
): void {
  appendNormalizedNode(outputDocument, parentNode, node, {
    normalizeHref: comparableHref,
    preserveSpaces,
  })
}

function appendUnsupportedBlockChildren(
  outputDocument: Document,
  parentNode: Element,
  node: Element,
  options: NormalizeNodeOptions
): void {
  const container = outputDocument.createElement('div')

  for (const childNode of node.childNodes) {
    appendNormalizedNode(outputDocument, container, childNode, options)
  }

  flushSanitizedBlockChildren(outputDocument, parentNode, container, options.preserveSpaces)
}

function isListContainer(node: Node): boolean {
  return isElementNode(node) && ['UL', 'OL'].includes(node.tagName)
}

function flushSanitizedListChildren(
  outputDocument: Document,
  parentNode: Element,
  container: Element,
  preserveSpaces: boolean
): void {
  let listItem: HTMLLIElement | null = null

  const flushListItem = () => {
    if (listItem && hasMeaningfulListItemContent(listItem, preserveSpaces)) {
      parentNode.append(listItem)
    }

    listItem = null
  }

  const ensureListItem = () => {
    if (!listItem) {
      listItem = outputDocument.createElement('li')
    }

    return listItem
  }

  const appendParagraphAsListItem = (paragraphNode: Element) => {
    const item = outputDocument.createElement('li')

    while (paragraphNode.firstChild) {
      item.append(paragraphNode.firstChild)
    }

    if (hasMeaningfulListItemContent(item, preserveSpaces)) {
      parentNode.append(item)
    }
  }

  for (const childNode of Array.from(container.childNodes)) {
    if (isElementNode(childNode) && childNode.tagName === 'LI') {
      flushListItem()
      parentNode.append(childNode)
      continue
    }

    if (isElementNode(childNode) && childNode.tagName === 'P') {
      flushListItem()
      appendParagraphAsListItem(childNode)
      continue
    }

    if (isElementNode(childNode) && ['UL', 'OL'].includes(childNode.tagName)) {
      ensureListItem().append(childNode)
      continue
    }

    ensureListItem().append(childNode)
  }

  flushListItem()
}

function flushSanitizedBlockChildren(
  outputDocument: Document,
  parentNode: Element,
  container: Element,
  preserveSpaces: boolean
): void {
  if (isListContainer(parentNode)) {
    flushSanitizedListChildren(outputDocument, parentNode, container, preserveSpaces)
    return
  }

  let paragraph: HTMLParagraphElement | null = null

  const flushParagraph = () => {
    if (paragraph && hasMeaningfulChildContent(paragraph, preserveSpaces)) {
      parentNode.append(paragraph)
    }

    paragraph = null
  }

  for (const childNode of Array.from(container.childNodes)) {
    if (isElementNode(childNode) && ['P', 'UL', 'OL'].includes(childNode.tagName)) {
      flushParagraph()
      parentNode.append(childNode)
      continue
    }

    if (!paragraph) {
      paragraph = outputDocument.createElement('p')
    }

    paragraph.append(childNode)
  }

  flushParagraph()
}

function sanitizeRichTextHtml(html: unknown, preserveSpaces: boolean): string | null {
  const sourceDocument = parseRichTextHtml(html)

  if (!sourceDocument?.body) {
    return null
  }

  const outputDocument = sourceDocument.implementation.createHTMLDocument('')
  const root = outputDocument.createElement('div')
  const container = outputDocument.createElement('div')

  for (const childNode of sourceDocument.body.childNodes) {
    appendSanitizedNode(outputDocument, container, childNode, preserveSpaces)
  }

  flushSanitizedBlockChildren(outputDocument, root, container, preserveSpaces)

  const sanitizedHtml = root.innerHTML.trim()

  return sanitizedHtml || null
}

function escapeHtml(text: string): string {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function isBlockTag(tagName: string): boolean {
  return BLOCK_TAGS.has(tagName.toLowerCase())
}

export function richTextHtmlToPlainText(
  html: unknown,
  { preserveSpaces = false }: RichTextOptions = {}
): string | null {
  const document = parseRichTextHtml(html)

  if (!document?.body) {
    return null
  }

  const plainText = blocksFromNode(document.body, preserveSpaces)
    .filter((block) => block.trim() !== '')
    .join('\n\n')

  return (preserveSpaces ? plainTextKeepingSpaces(plainText) : plainText.trim()) || null
}

export function normalizeRichTextHtml(html: unknown, options: RichTextOptions = {}): string | null {
  const normalizedHtml = sanitizeRichTextHtml(html, options.preserveSpaces ?? false)

  return normalizedHtml && richTextHtmlToPlainText(normalizedHtml, options) ? normalizedHtml : null
}

export function normalizeRichTextHtmlForEditorSync(
  html: unknown,
  options: RichTextOptions = {}
): string | null {
  const preserveSpaces = options.preserveSpaces ?? false
  const sourceDocument = parseRichTextHtml(html)

  if (!sourceDocument?.body) {
    return null
  }

  const outputDocument = sourceDocument.implementation.createHTMLDocument('')
  const root = outputDocument.createElement('div')
  const container = outputDocument.createElement('div')

  for (const childNode of sourceDocument.body.childNodes) {
    appendComparableNode(outputDocument, container, childNode, preserveSpaces)
  }

  flushSanitizedBlockChildren(outputDocument, root, container, preserveSpaces)

  const normalizedHtml = root.innerHTML.trim()

  if (!normalizedHtml) {
    return null
  }

  return normalizeRichTextHtml(normalizedHtml, options) ? normalizedHtml : null
}

/**
 * Plain text a florist typed, trimmed. With `preserveSpaces`, only line endings and trailing
 * whitespace change, as the API's `RichTextDescription::normalizePlainText()` does: CRLF becomes
 * LF, each line loses its trailing whitespace, and blank lines before the first and after the last
 * line drop, so a recipe grid's first line keeps the spaces that line up its columns.
 */
export function normalizePlainText(
  text: string,
  { preserveSpaces = false }: RichTextOptions = {}
): string {
  return preserveSpaces
    ? text
        .replace(/\r\n?/g, '\n')
        .replace(/[^\S\n]+$/gm, '')
        .replace(/^\n+|\n+$/g, '')
    : text.trim()
}

/**
 * Plain text as paragraphs, split at blank lines, with a line break per line. With
 * `preserveSpaces`, every space is kept.
 */
export function plainTextToRichTextHtml(
  text: unknown,
  { preserveSpaces = false }: RichTextOptions = {}
): string | null {
  if (typeof text !== 'string') {
    return null
  }

  const normalizedText = normalizePlainText(text, { preserveSpaces })

  if (!normalizedText) {
    return null
  }

  return normalizedText
    .split(/\n{2,}/)
    .map(
      (paragraph) =>
        `<p>${paragraph
          .split('\n')
          .map((line) =>
            preserveSpaces
              ? escapeHtml(spacesThatSurvive(line)).replaceAll(NO_BREAK_SPACE, '&nbsp;')
              : escapeHtml(line)
          )
          .join('<br>')}</p>`
    )
    .join('')
}
